import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { CurrencyPipe } from '@angular/common';

import {
  ButtonComponent,
  IconColor,
  IconComponent,
  IconName,
  InputComponent,
  SelectComponent,
  ToastService,
} from '@rp/shared/components';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HttpErrorHandlerService, PayoutsService } from '@rp/shared/services';
import {
  DEFAULT_PAGINATION,
  PaginationRequest,
  PaymentMethod,
  PayoutStatus,
} from '@rp/shared/models';
import { HttpPayoutsService, PAYOUTS_PROVIDER_TOKEN } from '@rp/shared/providers';
import { financesAmountMinValueValidator } from '@rp/shared/validators';
import { StructuresService, UserAuthService } from '@rp/core';

import { FINANCE_WEBMASTER_PROVIDER_TOKEN, HttpFinanceWebmasterProvider } from '../../providers';

@Component({
  selector: 'rp-payout-card',
  standalone: true,
  templateUrl: './payout-card.component.html',
  styleUrl: './payout-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SelectComponent,
    ReactiveFormsModule,
    InputComponent,
    ButtonComponent,
    TranslateModule,
    IconComponent,
  ],
  providers: [
    { provide: FINANCE_WEBMASTER_PROVIDER_TOKEN, useClass: HttpFinanceWebmasterProvider },
    { provide: PAYOUTS_PROVIDER_TOKEN, useClass: HttpPayoutsService },
    PayoutsService,
    CurrencyPipe,
  ],
})
export class PayoutCardComponent implements OnInit {
  form = new FormGroup({
    amountCents: new FormControl(null, [Validators.required, financesAmountMinValueValidator]),
    walletId: new FormControl(null, [Validators.required]),
  });
  iconColorEnum = IconColor;
  iconName = IconName;

  payoutStatus = signal<PayoutStatus>(PayoutStatus.Enabled);
  payoutStatusEnabled = computed(() => this.payoutStatus() === PayoutStatus.Enabled);

  paymentMethods = signal<PaymentMethod[]>([]);
  activePaymentMethods = computed<PaymentMethod[]>(() =>
    this.paymentMethods().filter(method => method.isActive),
  );

  getPayouts = output();

  structures = inject(StructuresService);

  private readonly _currencyPipe = inject(CurrencyPipe);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _financeWebmasterProvider = inject(FINANCE_WEBMASTER_PROVIDER_TOKEN);
  private readonly _httpErrorHandlerService = inject(HttpErrorHandlerService);
  private readonly _payoutsService = inject(PayoutsService);
  private readonly _toast = inject(ToastService);
  private readonly _translateService = inject(TranslateService);
  private readonly _userAuthService = inject(UserAuthService);

  ngOnInit(): void {
    this._getPayouts();

    this.payoutStatus.set(this._userAuthService.getPersonalInfoFromStorage().payoutStatus);
  }

  makePayout(): void {
    this._financeWebmasterProvider
      .makePayout({
        ...this.form.value,
        amountCents: this._toCents(Number(this.form.value.amountCents)),
      })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this._toast.showToast({
            text: this._translateService.instant('actions.toast.successfullyPayoutSubject', {
              method: this.activePaymentMethods()[0].walletName,
              amount: this._currencyPipe.transform(this.form.value.amountCents, 'USD'),
            }),
            type: 'success',
            icon: '',
            title: 'actions.toast.successfullyPayoutTitle',
          });
          this.form.reset();
          this.form.markAsPristine();
          this.getPayouts.emit();
        },
        error: (error: HttpErrorResponse) => this._httpErrorHandlerService.showErrorMessage(error),
      });
  }

  onScroll(): void {
    if (this.paymentMethods().length % 5 === 0) {
      this._getPayouts({ page: 1, limit: this.paymentMethods().length + 5 });
    }
  }

  private _getPayouts(pagination?: PaginationRequest): void {
    this._payoutsService
      .getPayouts(pagination ?? DEFAULT_PAGINATION)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: ({ payoutMethod }) => this.paymentMethods.set(payoutMethod),
        error: (error: HttpErrorResponse) => this._httpErrorHandlerService.showErrorMessage(error),
      });
  }

  private _toCents(value: number): number {
    return value * 100;
  }
}
